import { useRef } from 'react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

type UsePDFExportOptions = {
  pdfFormat?: 'a6' | 'a5' | 'a4'
  scale?: number
  oversize?: number
  defaultFilename?: string // fallback if nothing is passed during export
}

export const usePDFExport = ({
  pdfFormat = 'a6',
  scale = 2,
  oversize = 1.005,
  defaultFilename = 'exported.pdf'
}: UsePDFExportOptions = {}) => {
  const ref = useRef<HTMLDivElement | null>(null)

  const exportPDF = async (customFilename?: string) => {
    const input = ref.current
    if (!input) {
      console.warn('ExportPDF: ref is not attached.')
      return
    }

    const canvas = await html2canvas(input, {
      scale,
      useCORS: true
      // imageTimeout: 0
    })

    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF('p', 'mm', pdfFormat)
    ;(pdf as any).internal.scaleFactor = 1

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    const pxToMm = (px: number) => px * 0.264583
    const canvasWidthMm = pxToMm(canvas.width)
    const canvasHeightMm = pxToMm(canvas.height)

    const pageRatio = pageWidth / pageHeight
    const canvasRatio = canvasWidthMm / canvasHeightMm

    let imgWidth = pageWidth
    let imgHeight = pageHeight

    if (canvasRatio > pageRatio) {
      imgHeight = pageWidth / canvasRatio
    } else {
      imgWidth = pageHeight * canvasRatio
    }

    imgWidth *= oversize
    imgHeight *= oversize

    const x = (pageWidth - imgWidth) / 2
    const y = (pageHeight - imgHeight) / 2

    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight, undefined, 'FAST')

    // Use dynamic filename if provided, else default
    pdf.save(customFilename || defaultFilename)
  }

  return { ref, exportPDF }
}
